import React, { useMemo } from 'react';

import { Box, Divider, ListItemButton, Typography } from '@mui/material';
import { withStyles } from 'tss-react/mui';
import { Close, InsertDriveFileOutlined, ListAlt, MoreHoriz } from '@mui/icons-material';
import {
  customMedicalDocumentUploadReasonLabel, MedicalNoteType, PartnerReferralActivity, PatientActivity,
  PatientActivityType, PatientAppointmentActivity, PatientAssessmentFormActivity, PatientDocumentType, PatientNoteActivity,
  PatientPsychiatryAppointmentActivity, PatientStaticDocument, PatientTherapyAppointmentActivity,
  PatientUploadedMedicalDocument
} from '@uplift/api';
import { useTimezone } from '@uplift/contexts';
import { useL10n } from '@uplift/hooks';
import { HandshakeIcon, UpliftUIcon, VolunteerActivismIcon } from '@uplift/icons';
import { useAssetQuery, useUserProfileQuery } from '@uplift/queries';
import { CalendarDateService, DateObject } from '@uplift/services';
import { Form } from '@uplift/types';
import { toKebabCase } from '@uplift/utils';

import { LightChip } from '../../../../../LightChip';
import { getSessionTitle } from '../helpers';
import { documentsMapping } from '../PatientStaticDocument';
import { HighlightableAvatar } from './HighlightableAvatar.component';

const StyledListItem = withStyles(ListItemButton, theme => ({
  root: {
    '&:after': {
      content: '""',
      position: 'absolute',
      width: '0px',
      height: '100%',
      top: '0px',
      left: '0px',
      backgroundColor: theme.palette.secondary.main,
      transitionDuration: '60ms',
    },

    '&.Mui-selected': {
      backgroundColor: theme.palette.common.white,
      cursor: 'default',

      '&:hover': {
        backgroundColor: theme.palette.common.white,
      }
    },
  },
  selected: {
    color: theme.palette.secondary.main,
    position: 'relative' as const,

    '&:after': {
      transitionDuration: '140ms',
      width: '4px',
    },
  },
  disabled: {
    backgroundColor: theme.palette.grey[100],
  }
}));


type BaseChartMenuItemProps = {
  selected: boolean;
  disabled?: boolean;
  onSelect?: () => void;
  avatar: React.ReactNode;
  chip?: React.ReactNode;
  label: string;
  date: DateObject;
  testId?: string;
};


const BaseSidebarActivityItem: React.VFC<BaseChartMenuItemProps> = ({ avatar, label, date, onSelect, selected, disabled, chip, testId }) => {
  const timezone = useTimezone();
  const rootRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (selected && rootRef.current) {
      rootRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [ selected ]);

  return (
    <>
      <StyledListItem
        ref={rootRef}
        selected={selected}
        onClick={onSelect}
        disabled={disabled}
        dense
        data-testid={testId ?? toKebabCase(label)}
      >
        <Box display="flex" alignItems="center" width="100%" py={2}>
          {avatar}
          <Box ml={1} display="flex" gap="8px">
            <Typography style={{ fontWeight: 500 }}>
              {label}
            </Typography>
            {chip}
          </Box>
          <Box flex="1" display="flex" justifyContent="flex-end">
            <Typography component={selected ? 'strong' : 'span'} variant="body2" color="inherit" align="right">
              {CalendarDateService.format(date, 'MMM D', 'MMM D, YY', { timezone })}
            </Typography>
          </Box>
        </Box>
      </StyledListItem>
      <Divider />
    </>
  );
};


export const SessionSidebarActivityItem: React.VFC<{
  selected: boolean;
  disabled?: boolean;
  onSelect?: () => void;
  activityItem: PatientAppointmentActivity;
}> = ({ activityItem, ...props }) => {
  const label = React.useMemo(() => {
    return 'Cancelled Session';
  }, []);

  const { data: providerProfile } = useUserProfileQuery(activityItem.provider.id);
  const { data: avatarAsset } = useAssetQuery(providerProfile?.avatarAssetId);

  return (
    <BaseSidebarActivityItem
      label={label}
      date={CalendarDateService.date(activityItem.datetime)}
      avatar={(
        <HighlightableAvatar
          variant="circular"
          src={avatarAsset?.url}
          highlighted={props.selected}
        />
      )}
      {...props}
    />
  );
};

export const SessionNoteSidebarActivityItem: React.VFC<{
  selected: boolean;
  disabled?: boolean;
  onSelect?: () => void;
  activityItem: PatientTherapyAppointmentActivity | PatientPsychiatryAppointmentActivity;
}> = ({ activityItem, ...props }) => {
  const l10n = useL10n();
  const label = React.useMemo(() => {
    return getSessionTitle(l10n, activityItem.appointment, activityItem.isFirstSession);
  }, [ activityItem, l10n ]);

  const { data: providerProfile } = useUserProfileQuery(activityItem.provider.id);
  const { data: avatarAsset } = useAssetQuery(providerProfile?.avatarAssetId);
  const isArchived = activityItem.activityType === PatientActivityType.psychiatrySessionCompletedOnCanvas;

  return (
    <BaseSidebarActivityItem
      label={label}
      date={CalendarDateService.date(activityItem.datetime)}
      avatar={(
        <HighlightableAvatar
          variant="circular"
          src={avatarAsset?.url}
          highlighted={props.selected}
        />
      )}
      disabled={isArchived}
      chip={isArchived && <LightChip label="Unavailable" size="small" variant="outlined" />}
      {...props}
    />
  );
};


export const FormSidebarActivityItem: React.VFC<{
  selected: boolean;
  disabled?: boolean;
  onSelect?: () => void;
  datetime: string;
  form: Form;
}> = ({ form, datetime, ...props }) => {
  return (
    <BaseSidebarActivityItem
      label={form.name}
      date={CalendarDateService.date(datetime)}
      avatar={(
        <HighlightableAvatar variant="circular" highlighted={props.selected}>
          <ListAlt fontSize="small" />
        </HighlightableAvatar>
      )}
      {...props}
    />
  );
};


export const AssessmentInitialSidebarActivityItem: React.VFC<{
  selected: boolean;
  disabled?: boolean;
  onSelect?: () => void;
  activityItem: PatientAssessmentFormActivity;
}> = ({ activityItem, ...props }) => {
  return (
    <BaseSidebarActivityItem
      label={activityItem.form.name + ' Assessment'}
      date={CalendarDateService.date(activityItem.datetime)}
      avatar={(
        <HighlightableAvatar variant="circular" highlighted={props.selected}>
          <MoreHoriz />
        </HighlightableAvatar>
      )}
      {...props}
    />
  );
};


export const AssessmentExpiredSidebarActivityItem: React.VFC<{
  selected: boolean;
  disabled?: boolean;
  onSelect?: () => void;
  activityItem: PatientAssessmentFormActivity;
}> = ({ activityItem, ...props }) => {
  return (
    <BaseSidebarActivityItem
      label={activityItem.form.name + ' Assessment'}
      date={CalendarDateService.date(activityItem.datetime)}
      avatar={(
        <HighlightableAvatar variant="circular" highlighted={props.selected}>
          <Close />
        </HighlightableAvatar>
      )}
      {...props}
    />
  );
};

export const AssessmentSidebarActivityItem: React.VFC<{
  selected: boolean;
  disabled?: boolean;
  onSelect?: () => void;
  activityItem: PatientAssessmentFormActivity;
}> = ({ activityItem, ...props }) => {
  return (
    <BaseSidebarActivityItem
      label={activityItem.form.name + ' Assessment'}
      date={CalendarDateService.date(activityItem.datetime)}
      avatar={(
        <HighlightableAvatar variant="circular" highlighted={props.selected}>
          {activityItem.score}
        </HighlightableAvatar>
      )}
      {...props}
    />
  );
};

export const StaticDocumentSidebarActivityItem: React.VFC<{
  selected: boolean;
  disabled?: boolean;
  onSelect?: () => void;
  document: PatientStaticDocument;
}> = ({ document, ...props }) => {
  return (
    <BaseSidebarActivityItem
      label={documentsMapping[document.documentKey].title}
      date={CalendarDateService.date(document.uploadedAt)}
      avatar={(
        <HighlightableAvatar variant="circular" highlighted={props.selected}>
          <InsertDriveFileOutlined fontSize="small" />
        </HighlightableAvatar>
      )}
      {...props}
    />
  );
};

export type NoteSidebarActivityItemProps = {
  selected: boolean;
  disabled?: boolean;
  onSelect?: () => void;
  activityItem: PatientNoteActivity;
};

export const NoteSidebarActivityItem: React.VFC<NoteSidebarActivityItemProps> = ({ activityItem, ...props }) => {
  const { data: providerProfile } = useUserProfileQuery(activityItem.provider.id);
  const { data: avatarAsset } = useAssetQuery(providerProfile?.avatarAssetId);

  const label = useMemo(() => {
    if (activityItem.noteType === MedicalNoteType.communicationNote) {
      return 'Communication Note';
    }
    if (activityItem.noteType === MedicalNoteType.claimCorrectionNote) {
      return 'Claim Correction';
    }
    return 'Discharge Note';

  }, [ activityItem.noteType ]);

  const avatar = useMemo(() => {
    if (activityItem.noteType === MedicalNoteType.claimCorrectionNote) {
      return (
        <HighlightableAvatar variant="circular" highlighted={props.selected}>
          <UpliftUIcon color="secondary" />
        </HighlightableAvatar>
      );
    }
    return (
      <HighlightableAvatar
        variant="circular"
        src={avatarAsset?.url}
        highlighted={props.selected}
      />
    );
  }, [ activityItem.noteType, avatarAsset?.url, props.selected ]);

  return (
    <BaseSidebarActivityItem
      label={label}
      date={CalendarDateService.date(activityItem.note.submittedAt)}
      avatar={avatar}
      {...props}
    />
  );
};


export const UploadedDocumentSidebarActivityItem: React.VFC<{
  selected: boolean;
  disabled?: boolean;
  onSelect?: () => void;
  document: PatientUploadedMedicalDocument;
}> = ({ document, ...props }) => {
  return (
    <BaseSidebarActivityItem
      label={customMedicalDocumentUploadReasonLabel[document.reasonForUpload] ?? document.reasonForUpload}
      date={CalendarDateService.date(document.createdAt)}
      avatar={(
        <HighlightableAvatar variant="circular" highlighted={props.selected}>
          <InsertDriveFileOutlined fontSize="small" />
        </HighlightableAvatar>
      )}
      {...props}
    />
  );
};

export const ReferralSidebarActivityItem: React.VFC<{
  selected: boolean;
  disabled?: boolean;
  onSelect?: () => void;
  activityItem: PartnerReferralActivity;

}> = ({ activityItem, ...props }) => {

  return (
    <BaseSidebarActivityItem
      label="Partner Referral"
      date={CalendarDateService.date(activityItem.datetime)}
      avatar={(
        <HighlightableAvatar variant="circular" highlighted={props.selected}>
          <HandshakeIcon fontSize="small" />
        </HighlightableAvatar>
      )}
      {...props}
    />
  );
};

export const PsychReferralSidebarActivityItem: React.VFC<{
  selected: boolean;
  disabled?: boolean;
  onSelect?: () => void;
  activityItem: PatientActivity;

}> = ({ activityItem, ...props }) => {

  return (
    <BaseSidebarActivityItem
      label="Psychiatry Referral"
      date={CalendarDateService.date(activityItem.datetime)}
      avatar={(
        <HighlightableAvatar variant="circular" highlighted={props.selected}>
          <VolunteerActivismIcon fontSize="small" />
        </HighlightableAvatar>
      )}
      {...props}
    />
  );
};


export const SidebarActivityItem: React.VFC<{
  activityItem: PatientActivity;
  selected: boolean;
  onSelectItem: () => void;
}> = React.memo(({ activityItem, selected, onSelectItem }) => {
  switch (activityItem.activityType) {
    case PatientActivityType.document:
      switch (activityItem.document.type) {
        case PatientDocumentType.static:
          return (
            <StaticDocumentSidebarActivityItem
              key={activityItem.datetime}
              selected={selected}
              onSelect={onSelectItem}
              document={activityItem.document}
            />
          );
        case PatientDocumentType.medicalRecord:
          return (
            <UploadedDocumentSidebarActivityItem
              key={activityItem.datetime}
              selected={selected}
              onSelect={onSelectItem}
              document={activityItem.document}
            />
          );
      }

      return null;
    case PatientActivityType.therapySessionCancelled:
    case PatientActivityType.psychiatrySessionCancelled:
      return (
        <SessionSidebarActivityItem
          key={activityItem.datetime}
          selected={selected}
          onSelect={onSelectItem}
          activityItem={activityItem}
        />
      );
    case PatientActivityType.psychiatrySessionInitial:
    case PatientActivityType.psychiatrySessionSubmitted:
    case PatientActivityType.psychiatrySessionCompleted:
    case PatientActivityType.therapySessionCompleted:
    case PatientActivityType.therapySessionSubmitted:
      return (
        <SessionNoteSidebarActivityItem
          key={activityItem.datetime}
          selected={selected}
          onSelect={onSelectItem}
          activityItem={activityItem}
        />
      );
    case PatientActivityType.assessmentFormInitial:
      return (
        <AssessmentInitialSidebarActivityItem
          key={activityItem.datetime}
          selected={selected}
          onSelect={onSelectItem}
          activityItem={activityItem}
        />
      );
    case PatientActivityType.assessmentFormCompleted:
      return (
        <AssessmentSidebarActivityItem
          key={activityItem.datetime}
          selected={selected}
          onSelect={onSelectItem}
          activityItem={activityItem}
        />
      );
    case PatientActivityType.assessmentFormExpired:
      return (
        <AssessmentExpiredSidebarActivityItem
          key={activityItem.datetime}
          selected={selected}
          onSelect={onSelectItem}
          activityItem={activityItem}
        />
      );
    case PatientActivityType.formInitial:
    case PatientActivityType.formExpired:
    case PatientActivityType.formCompleted:
      return (
        <FormSidebarActivityItem
          key={activityItem.datetime}
          selected={selected}
          onSelect={onSelectItem}
          form={activityItem.form}
          datetime={activityItem.datetime}
        />
      );
    case PatientActivityType.noteSubmitted:
      return (
        <NoteSidebarActivityItem
          key={activityItem.datetime}
          selected={selected}
          onSelect={onSelectItem}
          activityItem={activityItem}
        />
      );
    case PatientActivityType.referral:
      return (
        <ReferralSidebarActivityItem
          key={activityItem.datetime}
          selected={selected}
          onSelect={onSelectItem}
          activityItem={activityItem as PartnerReferralActivity} // TODO: Fix type
        />
      );
    case PatientActivityType.newMatchByReferral:
      return (
        <PsychReferralSidebarActivityItem
          key={activityItem.datetime}
          selected={selected}
          onSelect={onSelectItem}
          activityItem={activityItem}
        />
      );
    default:
  }

  return null;
});
